import { getAccessToken } from "./auth";

// ---------- Generic auth-aware HTTP helper (Stage 3 lessons/livekit/teachers) ----------

export async function api<T = unknown>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  body?: unknown,
): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${method} ${path} -> ${res.status} ${text.slice(0, 200)}`);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json() as Promise<T>;
  return (await res.text()) as unknown as T;
}

// ---------- Chat (avatar legacy) ----------

export type ChatRole = "system" | "user" | "assistant";

export interface ChatMessage {
  role: ChatRole;
  content: string;
}

export interface ChatCompletionResponse {
  id: string;
  model: string;
  choices: Array<{
    index: number;
    message: { role: ChatRole; content: string };
    finish_reason: string | null;
  }>;
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
}

export async function sendChat(messages: ChatMessage[]): Promise<string> {
  const data = await api<ChatCompletionResponse>("POST", "/api/chat", { messages });
  const content = data.choices?.[0]?.message?.content;
  if (typeof content !== "string") throw new Error("No content in response");
  return content;
}

// ---------- Domain types ----------

export type CefrLevel = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

export interface UserRow {
  id: string;
  name: string | null;
  target_lang: string;
  native_lang: string;
  level: CefrLevel | string | null;
  goal: string | null;
  created_at: number;
  last_seen_at: number;
  kc_sub: string | null;
  email: string | null;
  is_anonymous: number;
  plan: string;
  trial_ends_at: number | null;
  stripe_customer_id: string | null;
  organization_id: string | null;
}

export interface AnalyzedError {
  span: string;
  type: string;
  category: string;
  explanation: string;
}

export interface TurnAnalysis {
  corrected: string;
  errors: AnalyzedError[];
  vocabulary_used: string[];
  estimated_level: CefrLevel;
  topic: string;
}

export interface SessionSummary {
  session_id: string;
  user_id: string;
  started_at: number;
  ended_at: number;
  duration_s: number;
  turn_count_user: number;
  turn_count_assistant: number;
  error_count: number;
  errors_by_category: Array<{ category: string; type: string; count: number; example: string }>;
  new_vocab: string[];
  estimated_level: string | null;
  top_recurring_errors: Array<{ category: string; type: string; count: number; explanation: string }>;
}

export interface UserStats {
  user_id: string;
  total_sessions: number;
  total_turns_user: number;
  total_errors: number;
  current_level: string | null;
  top_recurring_errors: Array<{ category: string; type: string; count: number; explanation: string }>;
}

// ---------- /api/me ----------

export async function getMe(): Promise<{ user: UserRow | null }> {
  return api<{ user: UserRow | null }>("GET", "/api/me");
}

export interface ProfilePatch {
  name?: string | null;
  target_lang?: string | null;
  native_lang?: string | null;
  goal?: string | null;
}

export async function updateProfile(patch: ProfilePatch): Promise<{ user: UserRow | null }> {
  return api<{ user: UserRow | null }>("POST", "/api/me/profile", patch);
}

export interface PlacementAnswer {
  question: string;
  answer: string;
}

export interface PlacementResult {
  level: CefrLevel;
  detected_levels: CefrLevel[];
  total_errors: number;
  user: UserRow | null;
}

export async function submitPlacement(
  answers: PlacementAnswer[],
  opts: { target_lang?: string; native_lang?: string } = {},
): Promise<PlacementResult> {
  return api<PlacementResult>("POST", "/api/me/placement", { answers, ...opts });
}

export async function getUserStats(): Promise<UserStats> {
  return api<UserStats>("GET", "/api/me/stats");
}

// ---------- /api/session ----------

export async function startSession(opts: { targetLang?: string; nativeLang?: string } = {}): Promise<{
  session_id: string;
  user_id: string;
}> {
  return api<{ session_id: string; user_id: string }>("POST", "/api/session/start", opts);
}

export async function endSession(sessionId: string): Promise<SessionSummary> {
  return api<SessionSummary>("POST", `/api/session/${sessionId}/end`, {});
}

// ---------- /api/turn ----------

export interface TurnRequest {
  messages: ChatMessage[];
  sessionId?: string;
  targetLang?: string;
  nativeLang?: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface TurnResponse {
  reply: string;
  analysis: TurnAnalysis | null;
}

export async function sendTurn(req: TurnRequest): Promise<TurnResponse> {
  return api<TurnResponse>("POST", "/api/turn", req);
}

// ---------- Anonymous → authenticated linking ----------
// No backend endpoint at the moment — backend creates the row on first auth callback.
// Kept as a no-op so legacy callers (App.tsx) compile and silently succeed.

export async function linkAnonymous(): Promise<null> {
  return null;
}
